import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PropTypes from "prop-types";
import { InputTextarea } from "primereact/inputtextarea";
import Button from "../../components/inputs/button";
import SelectField from "../../components/inputs/select";
import infoSvg from "../../assets/svgs/info.svg";
import sparkleIcon from "../../assets/svgs/sparkle.svg";
import { Tooltip } from "react-tooltip";
import Toast from "../../components/toast";
import Loader from "../../components/misc/loader";
import { generateAudiencesV2Stream, submitAudienceFeedback, getMyGenerationDetail } from "../../services/agenticV2";
import { getCohortSizing } from "../../services/cohort";
import { getSession } from "../../services/auth";
import { getTagBuilderConfiguration } from "../../services/tag";
import JwtDecode from "jwt-decode";
import {
	DELAYS,
	MOCK_AUDIENCE_SIZING,
	getMockResultsForMode,
	getMockDebugInfoForMode,
} from "./mockData";
import { transformCriteriaFromApi } from "../../components/builder-v2/utils/criteriaTransforms";
import AudienceCard from "./AudienceCard";
import CollapsibleJSON from "./CollapsibleJSON";
import "./index.scss";

function ElapsedTimer({ startedAt }) {
	const [elapsed, setElapsed] = useState(0);
	useEffect(() => {
		const id = setInterval(
			() => setElapsed(Math.floor((Date.now() - startedAt) / 1000)),
			1000,
		);
		return () => clearInterval(id);
	}, [startedAt]);
	return <p className="elapsed-time">{elapsed}s</p>;
}

ElapsedTimer.propTypes = { startedAt: PropTypes.number.isRequired };

function AIAudienceExplorerV2({ demoMode = false, onSelectAudience = null, generationIdProp = null }) {
	const [prompt, setPrompt] = useState("");
	const [rfpFile, setRfpFile] = useState(null);
	const [targetingMode, setTargetingMode] = useState("full");
	const [loading, setLoading] = useState(false);
	const [progress, setProgress] = useState(null);
	const [totalSteps, setTotalSteps] = useState(5);
	const [toast, setToast] = useState(null);
	const [results, setResults] = useState(null);
	const [audienceSizing, setAudienceSizing] = useState({});
	const [sizingLoading, setSizingLoading] = useState({});
	const [sizingFailed, setSizingFailed] = useState({});
	const [amsFlavor, setAmsFlavor] = useState("full");
	const [generationId, setGenerationId] = useState(null);
	const [restoredFeedback, setRestoredFeedback] = useState({});
	// Silo this view operates against (sizing + builder config). Defaults to the
	// switcher's active silo; on rehydrate it follows the generation's own silo so
	// a cross-silo generation sizes/labels against the silo it was built for —
	// without changing the global switcher (mirrors the builder's local tagId).
	const [siloId, setSiloId] = useState(() => {
		const s = parseInt(localStorage.getItem("activeSilo"));
		return Number.isNaN(s) ? null : s;
	});
	const [crossSiloName, setCrossSiloName] = useState(null);
	const [collapsedCards, setCollapsedCards] = useState(new Set());
	const [sidebarBottom, setSidebarBottom] = useState(0);
	const [debugMode, setDebugMode] = useState(false);
	const [debugInfo, setDebugInfo] = useState(null);
	const [activeDebugTab, setActiveDebugTab] = useState("pipeline");
	const [expandedSections, setExpandedSections] = useState({});
	const [criteriaConfiguration, setCriteriaConfiguration] = useState([]);
	const [customUserDataConfiguration, setCustomUserDataConfiguration] = useState([]);
	const [modifierConfiguration, setModifierConfiguration] = useState([]);
	const fileInputRef = useRef(null);
	const { generationId: generationIdParam } = useParams();
	// Rehydrate trigger: embedded creator passes a prop; the standalone results
	// route (/ai-audience-explorer-v2/results/:generationId) passes a route param.
	const rehydrateId = generationIdProp || generationIdParam;

	const targetingModeOptions = [
		{
			label: "Contextual Criteria Only",
			value: "contextual_only",
			description: "Target based on page content, technographics, and location only"
		},
		{
			label: "1st Party Criteria Only",
			value: "without_third_party",
			description: "Use all available targeting criteria excluding 3rd party segments (no additional fees)"
		},
		{
			label: "1st & 3rd Party Criteria",
			value: "full",
			description: "Use all available targeting criteria including 3rd party segments (Bombora, Experian, LiveRamp, Genius Sports, Retail Innovation Lab). Targeting 3rd party segments incurs an extra fee."
		},
	];

	useEffect(() => {
		document.title = "AI Audience Explorer";
		const settings = JSON.parse(localStorage.getItem("publisherSettings")) ?? {};
		setAmsFlavor(settings.ams_flavor ?? "full");
	}, []);

	// Always load builder config for the view's silo (standalone and embedded
	// alike), so criteria labels and custom-user-data integrations match the
	// silo the suggestions belong to — including a cross-silo rehydrate (ASB-3052).
	useEffect(() => {
		const loadBuilderConfig = async () => {
			try {
				if (!siloId) return;
				const builderConfig = await getTagBuilderConfiguration(siloId);
				const publisherName = builderConfig?.publisherName ?? "";
				const config = builderConfig?.configuration?.siteContent ?? {};
				setCriteriaConfiguration(config.criteria ?? []);
				setModifierConfiguration((config.modifier ?? []).map((el) => ({
					...el,
					publisherName,
				})));
				const cudIntegrations = (builderConfig?.configuration?.customUserData?.integrations ?? []).map((el) => ({
					...el,
					publisherName,
				}));
				setCustomUserDataConfiguration(cudIntegrations);
			} catch (error) {
				console.error("Failed to load builder configuration:", error);
			}
		};
		loadBuilderConfig();
	}, [siloId]);

	useEffect(() => {
		if (results && results.audiences) {
			fetchAudienceSizing(results.audiences);
		}
	}, [results]);

	// Rehydrate a prior generation's suggested-audience list without re-running
	// the pipeline (ASB-3052). Fired by the standalone /results/:id route param or
	// the embedded creator's generationIdProp. Sizing re-runs via the effect above.
	useEffect(() => {
		if (!rehydrateId || results || demoMode) return;
		let cancelled = false;
		(async () => {
			setLoading(true);
			try {
				const detail = await getMyGenerationDetail(rehydrateId);
				if (cancelled) return;
				const resp = detail?.response_json;
				if (resp?.audiences?.length) {
					setResults(resp);
					setGenerationId(rehydrateId);
					if (resp.debug_info) setDebugInfo(resp.debug_info);
					// Operate against the generation's own silo (sizing + config). If it
					// differs from the active silo, surface a banner rather than forcing
					// a switch (mirrors the builder's cross-silo behavior).
					if (detail.silo_id) {
						setSiloId(detail.silo_id);
						const activeSilo = parseInt(localStorage.getItem("activeSilo"));
						setCrossSiloName(
							detail.silo_id !== activeSilo ? (detail.silo_name || `Silo ${detail.silo_id}`) : null,
						);
					}
					// The API scopes detail.feedback to the current user, so just map it
					// by audience_index for the cards (ASB-3052).
					const mine = {};
					(detail.feedback || []).forEach((fb) => {
						mine[fb.audience_index] = fb;
					});
					setRestoredFeedback(mine);
					// Restore the "Your Input" section so "Generate Again" works without
					// re-entering anything (ASB-3052). The prompt is stored separately
					// from the file's extracted text.
					setPrompt(detail.targeting_text || "");
					// The original upload isn't stored — only its extracted text. Rebuild
					// a text file from it so it flows back through the normal path; it must
					// be .txt since the content is plain text, not the original binary
					// (processDocument parses by extension).
					if (detail.file_name && detail.file_text) {
						const base = detail.file_name.replace(/\.[^./\\]+$/, "");
						setRfpFile(new File([detail.file_text], `${base}.txt`, { type: "text/plain" }));
					}
				} else {
					setToast({ message: "This generation didn't produce any suggestions.", status: "Invalid", title: "Error" });
				}
			} catch (error) {
				if (!cancelled) {
					console.error("Error loading previous generation:", error);
					setToast({ message: "We couldn't load these suggestions. They may have been removed, or you may not have access.", status: "Invalid", title: "Error" });
				}
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
		// `results` intentionally excluded from deps — the guard above prevents a refetch loop.
	}, [rehydrateId]);

	// ESC key handler for debug panel
	useEffect(() => {
		const handleKeyDown = (event) => {
			if (event.key === "Escape" && debugMode) {
				setDebugMode(false);
			}
		};

		if (debugMode) {
			document.addEventListener("keydown", handleKeyDown);
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "unset";
		}

		return () => {
			document.removeEventListener("keydown", handleKeyDown);
			document.body.style.overflow = "unset";
		};
	}, [debugMode]);

	// Adjust sidebar bottom when footer becomes visible on scroll
	useLayoutEffect(() => {
		if (!results) {
			setSidebarBottom(0);
			return;
		}

		const updateSidebarBottom = () => {
			const footer = document.querySelector("footer");
			if (footer) {
				const footerRect = footer.getBoundingClientRect();
				const viewportHeight = window.innerHeight;
				if (footerRect.top < viewportHeight) {
					setSidebarBottom(viewportHeight - footerRect.top);
				} else {
					setSidebarBottom(0);
				}
			}
		};

		updateSidebarBottom();
		window.addEventListener("scroll", updateSidebarBottom, { passive: true });
		window.addEventListener("resize", updateSidebarBottom, { passive: true });

		return () => {
			window.removeEventListener("scroll", updateSidebarBottom);
			window.removeEventListener("resize", updateSidebarBottom);
		};
	}, [results]);

	const toggleSection = useCallback((key) => {
		setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
	}, []);

	const hasDebugAccess = () => {
		try {
			const session = getSession();
			if (session?.access_token) {
				const tokenData = JwtDecode(session.access_token);
				return tokenData.email === "jcabalugaz@arcspan.com";
			}
		} catch (error) {
			console.error("Error checking debug access:", error);
		}
		return false;
	};

	const formatMilliseconds = (ms) => {
		if (ms === undefined || ms === null) return "N/A";
		if (ms >= 1000) {
			return `${(ms / 1000).toFixed(2)}s`;
		}
		return `${ms.toFixed(2)}ms`;
	};

	const fetchSingleAudienceSizing = async (audience, index, siloId) => {
		const payload = {
			data: [{
				name: audience.name || `Audience ${index + 1}`,
				description: audience.description || "",
				tagId: siloId,
				cohort_type: "composite",
				schema_version: "2.0",
				auto_generated: true,
				lookback: "30d",
				activation_methods: [],
				gamNetworkSelectionMode: "all",
				composite_conditions_v2: audience.composite_conditions
			}]
		};

		const response = await getCohortSizing(payload);
		return response.json();
	};

	const fetchAudienceSizing = async (audiences) => {
		if (!audiences || audiences.length === 0) return;

		const loadingState = {};
		audiences.forEach((_, index) => { loadingState[index] = true; });
		setSizingLoading(loadingState);

		// Demo mode: Use mock sizing data with simulated delay
		if (demoMode) {
			setTimeout(() => {
				const sizing = {};
				audiences.forEach((_, index) => {
					sizing[index] = MOCK_AUDIENCE_SIZING[index] || { uniqueUsers: 0, pageViews: 0 };
				});
				setAudienceSizing(sizing);
				setSizingLoading({});
			}, DELAYS.SIZING);
			return;
		}

		if (!siloId) {
			console.error("No silo found for sizing");
			setSizingLoading({});
			return;
		}

		const results = await Promise.allSettled(
			audiences.map((audience, index) => fetchSingleAudienceSizing(audience, index, siloId))
		);

		let timeoutCount = 0;
		let failedCount = 0;
		const sizing = {};
		const failed = {};

		results.forEach((result, index) => {
			if (result.status === "fulfilled") {
				const data = result.value;
				if (data.status === "timeout") { timeoutCount++; failed[index] = true; }
				else if (data.status === "failed") { failedCount++; failed[index] = true; }
				sizing[index] = {
					uniqueUsers: data.uniqueUsers || 0,
					pageViews: data.pageViews || 0,
				};
			} else {
				failedCount++;
				failed[index] = true;
				sizing[index] = { uniqueUsers: 0, pageViews: 0 };
			}
		});

		setAudienceSizing(sizing);
		setSizingFailed(failed);
		setSizingLoading({});

		if (timeoutCount > 0) {
			setToast({
				message: timeoutCount === 1
					? "Timeout exceeded when sizing an audience. Please try again later."
					: `Timeout exceeded when sizing ${timeoutCount} audiences. Please try again later.`,
				status: "Invalid",
				title: "Sizing timeout",
			});
		} else if (failedCount > 0) {
			setToast({
				message: failedCount === 1
					? "Error encountered when sizing an audience. Please reach out to your ArcSpan representative for assistance."
					: `Error encountered when sizing ${failedCount} audiences. Please reach out to your ArcSpan representative for assistance.`,
				status: "Invalid",
				title: "Sizing error",
			});
		}
	};

	const handleFileChange = (e) => {
		const file = e.target.files[0];
		if (file) {
			const maxSize = 10 * 1024 * 1024;
			if (file.size > maxSize) {
				setToast({
					message: "File size exceeds 10 MB. Please upload a smaller file.",
					status: "Invalid",
					title: "File Too Large",
				});
				return;
			}

			const validTypes = [
				"application/pdf",
				"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
				"application/vnd.openxmlformats-officedocument.presentationml.presentation",
				"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
				"text/plain",
				"text/markdown",
			];
			if (!validTypes.includes(file.type)) {
				setToast({
					message: "Please upload a PDF, DOCX, PPTX, XLSX, TXT, or MD file",
					status: "Invalid",
					title: "Invalid File Type",
				});
				return;
			}
			setRfpFile(file);
		}
	};

	const handleDragOver = (e) => {
		e.preventDefault();
		e.stopPropagation();
	};

	const handleDrop = (e) => {
		e.preventDefault();
		e.stopPropagation();
		const file = e.dataTransfer.files[0];
		if (file) {
			handleFileChange({ target: { files: [file] } });
		}
	};

	const handleRemoveFile = () => {
		setRfpFile(null);
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		if (!prompt && !rfpFile) {
			setToast({
				message: "Please enter a description and/or upload an RFP file",
				status: "Invalid",
				title: "Input Required",
			});
			return;
		}

		setToast(null);
		setLoading(true);
		setAudienceSizing({});
		setSizingLoading({});

		// Demo mode: Use mock data with simulated delay
		if (demoMode) {
			setTimeout(() => {
				setResults(getMockResultsForMode(targetingMode));
				setDebugInfo(getMockDebugInfoForMode(targetingMode));
				setLoading(false);
			}, DELAYS.GENERATION);
			return;
		}

		try {
			const activeSiloId = parseInt(localStorage.getItem("activeSilo"));

			if (!activeSiloId) {
				throw new Error("Silo ID not found. Please select a silo/publisher.");
			}

			// A fresh generation always runs against the active silo — realign the
			// view's silo and clear any cross-silo banner left from a rehydrate.
			setSiloId(activeSiloId);
			setCrossSiloName(null);

			await generateAudiencesV2Stream(
				{
					siloId: activeSiloId,
					targetingText: prompt || undefined,
					rfpFile: rfpFile || undefined,
					targetingMode: amsFlavor === "contextual" ? "contextual_only" : targetingMode,
					includeDebug: hasDebugAccess(),
				},
				{
					onProgress: (event) => {
						if (event.type === "pipeline_start") {
							setTotalSteps(event.total_steps);
						} else if (event.type === "step_start") {
							setProgress({ step: event.step, label: event.label, startedAt: Date.now() });
						}
					},
					onComplete: (response) => {
						setResults(response);
						if (response.generation_id) {
							setGenerationId(response.generation_id);
						}
						if (response.debug_info) {
							setDebugInfo(response.debug_info);
						}
						setLoading(false);
						setProgress(null);
						// Standalone: promote to an addressable URL so the list survives
						// refresh and is the back target from the builder (ASB-3052).
						// Embedded (onSelectAudience set) stays inside the builder.
						if (!onSelectAudience && response.generation_id) {
							navigate(`/ai-audience-explorer-v2/results/${response.generation_id}`, { replace: true });
						}
					},
					onError: (err) => {
						setLoading(false);
						setProgress(null);
						const baseMessage = err.detail || "Generation failed";
						setToast({
							message: err.generation_id
								? `${baseMessage} (ID: ${err.generation_id})`
								: baseMessage,
							status: "Invalid",
							title: "Error",
						});
					},
				},
			);
		} catch (error) {
			console.error("Error generating audiences:", error);
			setToast({
				message: error.message || "Failed to generate audiences",
				status: "Invalid",
				title: "Error",
			});
			setLoading(false);
			setProgress(null);
		}
	};

	const count3PSegments = (audience) => {
		let count = 0;
		const cc = audience.composite_conditions;
		if (!cc) return 0;

		// Count from criteriaGroups if present (v2 schema)
		if (cc.criteriaGroups) {
			cc.criteriaGroups.forEach(group => {
				group.forEach(criterion => {
					const key = Object.keys(criterion)[0];
					if (key === "syndicated" && criterion.syndicated?.segments) {
						count += criterion.syndicated.segments.length;
					}
					if (key === "demographics") {
						const demo = criterion.demographics;
						if (demo) {
							Object.values(demo).forEach(val => {
								if (Array.isArray(val)) count += val.length;
							});
						}
					}
				});
			});
		}
		return count;
	};

	const navigate = useNavigate();

	const handleCreateAudience = (audience) => {
		const transformedGroups = (audience.composite_conditions?.criteriaGroups || []).map(
			(group) => group.map((apiCriteria) => transformCriteriaFromApi(apiCriteria))
		);
		const audienceData = {
			name: audience.name || "",
			description: audience.description || "",
			criteriaGroupCondition: (audience.composite_conditions?.criteriaGroupCondition || "and").toLowerCase(),
			criteriaGroups: transformedGroups.length > 0 ? transformedGroups : [[{ type: null, data: {} }]],
		};
		if (onSelectAudience) {
			onSelectAudience(audienceData, generationId, siloId);
		} else {
			navigate("/audience-builder-v2", { state: { aiAudienceData: audienceData, generationId, generationSiloId: siloId } });
		}
	};

	const handleSubmitFeedback = async (audienceIndex, rating, reasons, details) => {
		if (!generationId) return;
		try {
			await submitAudienceFeedback({
				generationId,
				audienceIndex,
				rating,
				reasons: reasons || undefined,
				details: details || undefined,
			});
		} catch (error) {
			console.error("Error submitting feedback:", error);
		}
	};

	const toggleCardCollapse = (cardIndex) => {
		setCollapsedCards((prev) => {
			const next = new Set(prev);
			if (next.has(cardIndex)) {
				next.delete(cardIndex);
			} else {
				next.add(cardIndex);
			}
			return next;
		});
	};

	// Debug UI
	const debugUI = (
		<>
			{hasDebugAccess() && debugInfo && (
				<button
					className="debug-floating-button"
					onClick={() => setDebugMode(!debugMode)}
					title="Debug Mode"
				>
					{debugMode ? "\u2715" : "\uD83D\uDC1B"}
				</button>
			)}

			{debugMode && debugInfo && (
				<div className="debug-panel" onClick={() => setDebugMode(false)}>
					<div className="debug-content" onClick={(e) => e.stopPropagation()}>
						<div className="debug-header">
							<h3>Debug Information (V2 Pipeline)</h3>
							<button
								className="debug-close-button"
								onClick={() => setDebugMode(false)}
							>
								Close
							</button>
						</div>

						<div className="debug-tabs">
							<button
								className={`debug-tab ${activeDebugTab === "pipeline" ? "active" : ""}`}
								onClick={() => setActiveDebugTab("pipeline")}
							>
								Pipeline Steps
							</button>
							<button
								className={`debug-tab ${activeDebugTab === "timing" ? "active" : ""}`}
								onClick={() => setActiveDebugTab("timing")}
							>
								Timing
							</button>
							<button
								className={`debug-tab ${activeDebugTab === "config" ? "active" : ""}`}
								onClick={() => setActiveDebugTab("config")}
							>
								Config
							</button>
						</div>

						<div className="debug-body">
							{activeDebugTab === "pipeline" && (
								<div className="debug-tab-content">
									{debugInfo.step1_extraction_response && (
										<CollapsibleJSON
											title="Step 1 - Extraction"
											data={debugInfo.step1_extraction_response}
											expandedSections={expandedSections}
											toggleSection={toggleSection}
											sectionKey="step1"
										/>
									)}

									{debugInfo.step2_audience_plan && (
										<CollapsibleJSON
											title="Step 2 - Audience Plan"
											data={debugInfo.step2_audience_plan}
											expandedSections={expandedSections}
											toggleSection={toggleSection}
											sectionKey="step2"
										/>
									)}

									{debugInfo.step3_criteria_data?.map((item, i) => (
										<CollapsibleJSON
											key={`step3-${i}`}
											title={`Step 3 - Criteria Data [${i}]`}
											data={item}
											expandedSections={expandedSections}
											toggleSection={toggleSection}
											sectionKey={`step3-${i}`}
										/>
									))}

									{debugInfo.step4_assembly_responses?.map((item, i) => (
										<CollapsibleJSON
											key={`step4-${i}`}
											title={`Step 4 - Assembly [${i}]`}
											data={item}
											expandedSections={expandedSections}
											toggleSection={toggleSection}
											sectionKey={`step4-${i}`}
										/>
									))}
								</div>
							)}

							{activeDebugTab === "timing" && (
								<div className="debug-tab-content">
									{debugInfo.timing && (
										<div className="debug-section">
											<h4>Performance Metrics</h4>
											<div className="debug-timing-hierarchy">
												<div className="timing-line timing-top">
													Total: {formatMilliseconds(debugInfo.timing.total)}
												</div>
												{debugInfo.timing.step1_extract !== undefined && (
													<div className="timing-line timing-sub">
														- Step 1 (Extract): {formatMilliseconds(debugInfo.timing.step1_extract)}
													</div>
												)}
												{debugInfo.timing.step2_plan !== undefined && (
													<div className="timing-line timing-sub">
														- Step 2 (Plan): {formatMilliseconds(debugInfo.timing.step2_plan)}
													</div>
												)}
												{debugInfo.timing.step3_build !== undefined && (
													<div className="timing-line timing-sub">
														- Step 3 (Build): {formatMilliseconds(debugInfo.timing.step3_build)}
													</div>
												)}
												{debugInfo.timing.step4_assemble !== undefined && (
													<div className="timing-line timing-sub">
														- Step 4 (Assemble): {formatMilliseconds(debugInfo.timing.step4_assemble)}
													</div>
												)}
												{debugInfo.timing.post_processing !== undefined && (
													<div className="timing-line timing-sub">
														- Post-Processing: {formatMilliseconds(debugInfo.timing.post_processing)}
													</div>
												)}
											</div>
										</div>
									)}

									{debugInfo.request_info && (
										<div className="debug-section">
											<h4>Model Information</h4>
											<div className="debug-key-value-grid">
												{debugInfo.request_info.models && (
													<>
														<div className="debug-key-value">
															<span className="key">Step 1 (Extract):</span>
															<span className="value">{debugInfo.request_info.models.extract_strategy}</span>
														</div>
														<div className="debug-key-value">
															<span className="key">Step 2 (Plan):</span>
															<span className="value">{debugInfo.request_info.models.plan_audiences}</span>
														</div>
														<div className="debug-key-value">
															<span className="key">Step 3 (Build):</span>
															<span className="value">{debugInfo.request_info.models.build_criteria}</span>
														</div>
														<div className="debug-key-value">
															<span className="key">Step 4 (Assemble):</span>
															<span className="value">{debugInfo.request_info.models.assemble_audience}</span>
														</div>
													</>
												)}
												{debugInfo.request_info.temperature !== undefined && (
													<div className="debug-key-value">
														<span className="key">Temperature:</span>
														<span className="value">{debugInfo.request_info.temperature}</span>
													</div>
												)}
											</div>
										</div>
									)}
								</div>
							)}

							{activeDebugTab === "config" && (
								<div className="debug-tab-content">
									{debugInfo.request_info && (
										<div className="debug-section">
											<h4>Request Configuration</h4>
											<table className="debug-table">
												<tbody>
													{Object.entries(debugInfo.request_info).map(([key, value]) => (
														<tr key={key}>
															<td className="debug-table-label">{key}</td>
															<td className="debug-table-value">
																{typeof value === "object" ? JSON.stringify(value) : String(value)}
															</td>
														</tr>
													))}
												</tbody>
											</table>
										</div>
									)}
								</div>
							)}
						</div>
					</div>
				</div>
			)}
		</>
	);

	// Render initial form
	if (!results) {
		return (
			<div className="wrapper" id="ai-audience-explorer-v2">
				<div className="initial-form">
					<div className="container">
						<div className="container--inner">
							<h1 className="title">What audience do you want to build?</h1>

							<form onSubmit={handleSubmit}>
								<div className="input-card">
									<InputTextarea
										id="prompt"
										value={prompt}
										onChange={(e) => setPrompt(e.target.value)}
										rows={4}
										placeholder={"Describe your audience and any goals.\ne.g. Create a 5M+ audience of tech-savvy young professionals interested in..."}
										disabled={loading}
									/>
									<div
										className="file-upload-area"
										onDragOver={handleDragOver}
										onDrop={handleDrop}
										onClick={() => fileInputRef.current?.click()}
									>
										<svg className="upload-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
											<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
											<polyline points="17 8 12 3 7 8" />
											<line x1="12" y1="3" x2="12" y2="15" />
										</svg>
										<div className="upload-content">
											<div className="upload-text">Add an RFP or media plan to analyze or use as context</div>
											<div className="upload-subtext">PDF, DOCX, PPTX, XLSX, TXT, MD up to 10MB</div>
										</div>
										<input
											ref={fileInputRef}
											type="file"
											accept=".pdf,.docx,.pptx,.xlsx,.txt,.md"
											onChange={handleFileChange}
											style={{ display: "none" }}
											disabled={loading}
										/>
									</div>
									{rfpFile && (
										<div className="file-selected">
											<span className="file-name">📎 {rfpFile.name}</span>
											<button
												type="button"
												onClick={handleRemoveFile}
												className="remove-button"
												disabled={loading}
											>
												Remove
											</button>
										</div>
									)}
								</div>

								{amsFlavor !== "contextual" && (
									<div className="form-section data-access-section">
										<label>
											Data Access
											<Tooltip id="data-access-tooltip" positionStrategy="fixed" place="right" />
											<img src={infoSvg} className="help-icon" alt="info" data-tooltip-id="data-access-tooltip" data-tooltip-html={targetingModeOptions.map(opt => `<b>${opt.label}</b><br/><span style="font-weight:300;opacity:0.85">${opt.description}</span>`).join("<br/><br/>")} />
										</label>
										<div className="segmented-control">
											{targetingModeOptions.map((opt) => (
												<button
													key={opt.value}
													type="button"
													className={`segment${targetingMode === opt.value ? " active" : ""}`}
													onClick={() => setTargetingMode(opt.value)}
													disabled={loading}
												>
													{opt.label}
												</button>
											))}
										</div>
									</div>
								)}

								<Button
									text={loading ? "Creating Audience..." : "Create Audience Options"}
									type="submit"
									style="dark"
									disabled={loading}
									loaded={loading}
								/>
							</form>
						</div>
					</div>
				</div>

				{loading && (
					<div className="loading-overlay">
						<Loader />
						{progress ? (
							<div className="step-progress">
								<p className="step-label">
									Step {progress.step} of {totalSteps}: {progress.label}...
								</p>
								<ElapsedTimer startedAt={progress.startedAt} />
							</div>
						) : (
							<p>Preparing to generate audience segments...</p>
						)}
					</div>
				)}

				{toast && (
					<Toast
						status={toast.status}
						message={toast.message}
						title={toast.title}
						callback={() => setToast(null)}
						autoHide={true}
					/>
				)}
			</div>
		);
	}

	// Render results view
	return (
		<div className="wrapper" id="ai-audience-explorer-v2">
			<Tooltip id="lookback-tooltip" />
			<div className="results-view">
				<div className="results-wrapper">
					<div className="sidebar" style={{ bottom: `${sidebarBottom}px` }}>
						<div className="sidebar-content">
							<h1 className="sidebar-title">Your Input</h1>
							<p className="sidebar-subtitle">
								Unhappy with the results? Edit your input and generate again.
							</p>

							<form onSubmit={handleSubmit} id="sidebar-form">
								<div className="input-card">
									<InputTextarea
										id="prompt-results"
										value={prompt}
										onChange={(e) => setPrompt(e.target.value)}
										rows={4}
										placeholder={"Describe your audience and any goals.\ne.g. Create a 5M+ audience of tech-savvy young professionals interested in..."}
										disabled={loading}
									/>
									<div
										className="file-upload-area"
										onDragOver={handleDragOver}
										onDrop={handleDrop}
										onClick={() => fileInputRef.current?.click()}
									>
										<svg className="upload-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
											<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
											<polyline points="17 8 12 3 7 8" />
											<line x1="12" y1="3" x2="12" y2="15" />
										</svg>
										<div className="upload-content">
											<div className="upload-text">Add an RFP or media plan to analyze or use as context</div>
											<div className="upload-subtext">PDF, DOCX, PPTX, XLSX, TXT, MD up to 10MB</div>
										</div>
										<input
											ref={fileInputRef}
											type="file"
											accept=".pdf,.docx,.pptx,.xlsx,.txt,.md"
											onChange={handleFileChange}
											style={{ display: "none" }}
											disabled={loading}
										/>
									</div>
									{rfpFile && (
										<div className="file-selected">
											<span className="file-name">📎 {rfpFile.name}</span>
											<button
												type="button"
												onClick={handleRemoveFile}
												className="remove-button"
												disabled={loading}
											>
												Remove
											</button>
										</div>
									)}
								</div>

								{amsFlavor !== "contextual" && (
									<div className="form-section data-access-section">
										<label>
											Data Access
											<Tooltip id="data-access-tooltip" positionStrategy="fixed" place="right" />
											<img src={infoSvg} className="help-icon" alt="info" data-tooltip-id="data-access-tooltip" data-tooltip-html={targetingModeOptions.map(opt => `<b>${opt.label}</b><br/><span style="font-weight:300;opacity:0.85">${opt.description}</span>`).join("<br/><br/>")} />
										</label>
										<SelectField
											name="targetingMode"
											required={false}
											disabled={loading}
											multi={false}
											selected={targetingModeOptions.find(opt => opt.value === targetingMode)}
											value={targetingModeOptions.find(opt => opt.value === targetingMode)}
											onSubmit={(selected) => setTargetingMode(selected.value)}
											options={targetingModeOptions}
										/>
									</div>
								)}
							</form>
						</div>

						<div className="sidebar-footer">
							<Button
								text={loading ? "Creating Audience..." : "Generate Again"}
								type="button"
								style="dark"
								disabled={loading}
								loaded={loading}
								onClick={() => document.getElementById("sidebar-form").requestSubmit()}
							/>
						</div>
					</div>

					<div className="main-content">
						{crossSiloName && (
							<div className="suggestions-silo-banner">
								<img src={infoSvg} alt="" className="banner-icon" />
								<span>
									These suggestions were generated for <strong>{crossSiloName}</strong>.
								</span>
							</div>
						)}
						<div className="ai-header">
							<div className="ai-header-top">
								<h2><img src={sparkleIcon} alt="" className="ai-icon" /> ArcSpan AI</h2>
							</div>
							<h3>Here is my understanding of the audience:</h3>
							<div className="ai-interpretation-box">
								<p className="ai-interpretation">
									{results.requirements_summary || results.strategy_analysis}
								</p>
							</div>
						</div>

						{/* Closest Match Section */}
						{results.audiences?.length > 0 && (
							<div className="audiences-section">
								<h3>I created this audience that matches the best:</h3>

								<div className="audiences-list">
									<AudienceCard
										audience={results.audiences[0]}
										cardIndex={0}
										sizingData={audienceSizing[0]}
										sizingIsLoading={sizingLoading[0]}
										sizingFailed={sizingFailed[0]}
										isCollapsed={collapsedCards.has(0)}
										onToggleCollapse={toggleCardCollapse}
										onCreateAudience={handleCreateAudience}
										count3PSegments={count3PSegments}
										showThemeBanner={false}
										generationId={generationId}
										onSubmitFeedback={handleSubmitFeedback}
										existingFeedback={restoredFeedback[0]}
										criteriaConfiguration={criteriaConfiguration}
										customUserDataConfiguration={customUserDataConfiguration}
										modifierConfiguration={modifierConfiguration}
									/>
								</div>
							</div>
						)}

						{/* Alternate Options Section */}
						{results.audiences?.length > 1 && (
							<div className="audiences-section">
								<h3>
									I explored {results.audiences.length - 1} variant
									{results.audiences.length - 1 !== 1 ? "s" : ""}:
								</h3>

								<div className="audiences-list">
									{results.audiences.slice(1).map((audience, index) => (
										<AudienceCard
											key={index + 1}
											audience={audience}
											cardIndex={index + 1}
											sizingData={audienceSizing[index + 1]}
											sizingIsLoading={sizingLoading[index + 1]}
											sizingFailed={sizingFailed[index + 1]}
											isCollapsed={collapsedCards.has(index + 1)}
											onToggleCollapse={toggleCardCollapse}
											onCreateAudience={handleCreateAudience}
											count3PSegments={count3PSegments}
											generationId={generationId}
											onSubmitFeedback={handleSubmitFeedback}
											existingFeedback={restoredFeedback[index + 1]}
											criteriaConfiguration={criteriaConfiguration}
											customUserDataConfiguration={customUserDataConfiguration}
											modifierConfiguration={modifierConfiguration}
										/>
									))}
								</div>
							</div>
						)}
					</div>
				</div>
			</div>

			{loading && (
				<div className="loading-overlay">
					<Loader />
					{progress ? (
						<div className="step-progress">
							<p className="step-label">
								Step {progress.step} of {totalSteps}: {progress.label}...
							</p>
							<ElapsedTimer startedAt={progress.startedAt} />
						</div>
					) : (
						<p>Preparing to generate audience segments...</p>
					)}
				</div>
			)}

			{toast && (
				<Toast
					status={toast.status}
					message={toast.message}
					title={toast.title}
					callback={() => setToast(null)}
					autoHide={true}
				/>
			)}

			{debugUI}
		</div>
	);
}

AIAudienceExplorerV2.propTypes = {
	demoMode: PropTypes.bool,
	onSelectAudience: PropTypes.func,
	generationIdProp: PropTypes.string,
};

export default AIAudienceExplorerV2;
